// src/components/RecommendedNextActionsModal.jsx
// ============================================================================
// RECOMMENDED NEXT ACTIONS MODAL
// ============================================================================
// Shows an intermediate dialog after a date change on the timeline, listing
// the recommended next actions the user can take. The user must explicitly
// choose an action (or skip) before any downstream workflow opens.
// ============================================================================
import React from 'react';
import { X, Clock, FileText, Mail, Bell, AlertTriangle, SkipForward } from 'lucide-react';

// ── Icon resolver ───────────────────────────────────────────────────────────

const ICON_MAP = {
  FileText,
  Mail,
  Bell,
  AlertTriangle,
};

// ── Category accent colours ─────────────────────────────────────────────────

const CATEGORY_COLORS = {
  invoice:  { bg: 'var(--brio-gold-light, rgba(159,144,42,0.1))',     border: 'var(--brio-gold, #9F902A)',     text: '#78700f' },
  email:    { bg: 'var(--brio-teal-light, rgba(104,149,129,0.12))',   border: 'var(--brio-teal, #689581)',     text: '#3d5e4e' },
  reminder: { bg: 'var(--brio-red-light, rgba(147,73,72,0.1))',       border: 'var(--brio-red, #934948)',      text: '#6b3534' },
  review:   { bg: 'var(--brio-orange-light, rgba(219,107,93,0.1))',   border: 'var(--brio-orange, #DB6B5D)',   text: '#8a4035' },
};

// ── Trigger labels ──────────────────────────────────────────────────────────

const TRIGGER_LABELS = {
  stage_closure:         'Actual date set — stage completed',
  invoice_sent:          'Invoice date set',
  timeline_change:       'Planned date modified',
  advance_paid:          'Advance invoice paid',
  invoice_paid:          'Stage invoice paid',
  payment_overdue:       'Payment overdue detected',
  missing_payment_terms: 'Missing payment terms',
};

// ── Action definition overrides (button labels) ─────────────────────────────

const ACTION_BUTTON_LABELS = {
  create_proforma_invoice:         'Create Proforma Invoice',
  create_tax_invoice:              'Create Tax Invoice',
  draft_project_initiated_email:   'Draft Project Initiated Email',
  draft_stage_initiation_email:    'Draft Stage Initiation Email',
  draft_stage_closure_email:       'Draft Stage Closure Email',
  draft_tax_invoice_email:         'Draft Tax Invoice Email',
  draft_timeline_change_email:     'Draft Timeline Change Email',
  draft_payment_reminder_email:    'Draft Payment Reminder Email',
  review_missing_payment_terms:    'Review Payment Terms',
};

// ── Main Component ──────────────────────────────────────────────────────────

export default function RecommendedNextActionsModal({
  context,       // { triggerType, stageName, stageKey, actions: [...actionObjects], projectId }
  actionDefs,    // ACTION_DEFS from useRecommendedActions
  onSelectAction,  // (action) => void
  onSkip,          // () => void — "Skip for now"
}) {
  if (!context || !context.actions || context.actions.length === 0) return null;

  const triggerLabel = TRIGGER_LABELS[context.triggerType] || 'Date updated';

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onSkip}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.35)',
          zIndex: 9998,
        }}
      />
      {/* Modal */}
      <div style={{
        position: 'fixed',
        top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 9999,
        background: '#fff',
        border: '1px solid var(--brio-border, #e5e5e5)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
        width: '420px',
        maxWidth: '92vw',
        maxHeight: '80vh',
        overflow: 'auto',
        fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px 10px',
          borderBottom: '1px solid var(--brio-border, #e5e5e5)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock style={{ width: 16, height: 16, color: 'var(--brio-gold, #9F902A)' }} />
            <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--brio-text, #1a1a1a)' }}>
              Recommended Next Actions
            </span>
          </div>
          <button
            onClick={onSkip}
            title="Skip for now"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: '2px', lineHeight: 1,
            }}
          >
            <X style={{ width: 16, height: 16, color: 'var(--brio-grey, #888)' }} />
          </button>
        </div>

        {/* Trigger description */}
        <div style={{
          padding: '10px 18px',
          fontSize: '11px',
          color: 'var(--brio-grey, #888)',
          background: 'var(--brio-grey-light, #fafafa)',
          borderBottom: '1px solid var(--brio-border, #e5e5e5)',
        }}>
          <span style={{ fontWeight: 500 }}>{triggerLabel}</span>
          {context.stageName && (
            <span> &mdash; {context.stageName}</span>
          )}
        </div>

        {/* Action list */}
        <div style={{ padding: '12px 18px' }}>
          {context.actions.map((action) => {
            const def = actionDefs?.[action.actionType] || {};
            const cat = CATEGORY_COLORS[def.category] || CATEGORY_COLORS.email;
            const Icon = ICON_MAP[def.icon] || Mail;
            const buttonLabel = ACTION_BUTTON_LABELS[action.actionType] || action.title || 'Open';

            return (
              <button
                key={action.id}
                onClick={() => onSelectAction(action)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  width: '100%',
                  padding: '10px 12px',
                  marginBottom: '6px',
                  background: cat.bg,
                  border: `1px solid ${cat.border}`,
                  borderLeft: `3px solid ${cat.border}`,
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
                  transition: 'opacity 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.85'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                <Icon style={{ width: 16, height: 16, color: cat.border, flexShrink: 0, marginTop: '1px' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '12px', color: cat.text, lineHeight: 1.3 }}>
                    {buttonLabel}
                  </div>
                  {action.description && (
                    <div style={{ fontSize: '10px', color: 'var(--brio-grey, #888)', marginTop: '2px', lineHeight: 1.3 }}>
                      {action.description}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Skip button */}
        <div style={{
          padding: '8px 18px 14px',
          borderTop: '1px solid var(--brio-border, #e5e5e5)',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <button
            onClick={onSkip}
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 14px',
              fontSize: '11px',
              fontWeight: 500,
              background: 'none',
              border: '1px solid var(--brio-border, #e5e5e5)',
              cursor: 'pointer',
              color: 'var(--brio-grey, #888)',
              fontFamily: 'var(--brio-font-family, Raleway, sans-serif)',
            }}
          >
            <SkipForward style={{ width: 12, height: 12 }} />
            Skip for now
          </button>
        </div>
      </div>
    </>
  );
}
